<template>
    <div class="history_card">
        <div class="card_header_row">
            <button class="card_toggle"
                :aria-expanded="isExpanded"
                @click="toggleExpanded">
                <ChartLine class="header_icon mr-2" />
                <span class="card_title">Historial</span>
                <ArrowDown class="chevron ml-2"
                    :class="{ 'is-rotated': isExpanded }" />
            </button>
            <div class="header_right is-flex is-align-items-center">
                <button class="comparison_btn" disabled title="Próximamente">
                    <ChartLine class="comparison_icon mr-1" />
                    Comparación
                </button>
            </div>
        </div>

        <transition name="history_collapse">
            <div v-if="isExpanded" class="card_body">
                <div class="legend_row is-flex is-align-items-center">
                    <span class="legend_item is-flex is-align-items-center">
                        <span class="legend_swatch swatch_amount"></span>
                        Monto ($)
                    </span>
                    <span class="legend_item is-flex is-align-items-center ml-4">
                        <span class="legend_swatch swatch_share"></span>
                        (%) de Ventas
                    </span>
                </div>

                <div class="chart_wrap">
                    <EChart v-if="chartReady && hasData"
                        :option="chartOption"
                        class="chart" />
                    <p v-else-if="!hasData" class="empty_state">
                        Sin actividad para el rango seleccionado.
                    </p>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import ChartLine from "@/components/svg/ChartLine.vue";
import { parseToMoney } from "@/utils/currency";

// Labels esp. compactos para el eje X — moment 'es' renderea "ene." con
// punto y minúsculas; nuestro diseño usa capitalizado sin punto ("Ene 2024").
const SPANISH_MONTH_ABBR = [
    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

function formatMonthLabel(monthIso) {
    if (!monthIso) return "";
    const [year, month] = monthIso.split("-");
    const idx = Number(month) - 1;
    if (idx < 0 || idx > 11) return monthIso;
    return `${SPANISH_MONTH_ABBR[idx]}\n${year}`;
}

function formatCompactMoney(value) {
    const abs = Math.abs(value || 0);
    // Sin numeric separators (ES2021) — el babel del proyecto en Node 14.16
    // no los reconoce y rompe la compilación con
    // "Identifier directly after number".
    if (abs >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (abs >= 1000000) return `$${Math.round(value / 1000000)}M`;
    if (abs >= 1000) return `$${Math.round(value / 1000)}K`;
    return `$${value || 0}`;
}

export default {
    name: "MonthlyHistoryCard",

    components: { EChart, ArrowDown, ChartLine },

    props: {
        /** `[{ month_iso, amount, sales_total }]` ordenado cronológicamente. */
        series: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            // Default expandido — la card más útil del drawer es el chart.
            // El toggle existe para liberar viewport cuando el usuario quiere
            // foco en la tabla de documentos (lista larga, lectura densa).
            isExpanded: true,
            // ECharts no resuelve var(--…) dentro del canvas — leemos los
            // tokens en mounted() y los inyectamos como hex. El v-if=chartReady
            // evita el flash con colores sin paleta.
            chartReady: false,
            colorAmount: "",
            colorShare: "",
            colorAxis: "",
            colorAxisLabel: "",
            colorGrid: "",
            colorTooltipBg: "",
            colorTooltipBorder: "",
        };
    },

    computed: {
        hasData() {
            return this.series.length > 0;
        },
        monthLabels() {
            return this.series.map((row) => formatMonthLabel(row.month_iso));
        },
        amounts() {
            return this.series.map((row) => row.amount || 0);
        },
        sharePercentages() {
            // Sales_total puede ser 0 (mes sin ventas para la empresa) — el
            // ratio queda en null para que la línea no caiga abruptamente al
            // origen, dejando un gap visual.
            return this.series.map((row) => {
                const total = row.sales_total || 0;
                if (total <= 0) return null;
                return Number(((row.amount || 0) / total * 100).toFixed(1));
            });
        },
        chartOption() {
            return {
                grid: {
                    left: 56,
                    right: 56,
                    top: 16,
                    bottom: 40,
                    containLabel: false,
                },
                tooltip: {
                    trigger: "axis",
                    backgroundColor: this.colorTooltipBg,
                    borderColor: this.colorTooltipBorder,
                    borderWidth: 1,
                    textStyle: { fontSize: 12 },
                    formatter: (params) => {
                        if (!params || !params.length) return "";
                        const month = params[0].axisValue.replace("\n", " ");
                        const lines = params.map((p) => {
                            const isShare = p.seriesName.includes("%");
                            const v = isShare
                                ? (p.value == null ? "—" : `${p.value}%`)
                                : parseToMoney(p.value || 0);
                            return `<div style="display:flex;align-items:center;gap:6px;">
                                <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${p.color};"></span>
                                ${p.seriesName}: <b>${v}</b>
                            </div>`;
                        }).join("");
                        return `<div style="font-weight:600;margin-bottom:4px;">${month}</div>${lines}`;
                    },
                },
                xAxis: {
                    type: "category",
                    data: this.monthLabels,
                    axisLine: { lineStyle: { color: this.colorAxis } },
                    axisTick: { show: false },
                    axisLabel: {
                        color: this.colorAxisLabel,
                        fontSize: 11,
                        interval: 0,
                        lineHeight: 14,
                    },
                },
                yAxis: [
                    {
                        type: "value",
                        position: "left",
                        axisLine: { show: false },
                        axisTick: { show: false },
                        splitLine: { lineStyle: { color: this.colorGrid } },
                        axisLabel: {
                            color: this.colorAxisLabel,
                            fontSize: 11,
                            formatter: formatCompactMoney,
                        },
                    },
                    {
                        // Sin `max: 100` — el rango real de un cliente sobre
                        // el total de ventas suele ser pequeño (p.ej. 2-15%).
                        // Con max fijo en 100 la línea queda pegada al piso
                        // y se pierde su variación entre meses. `scale: true`
                        // hace que ECharts ajuste min/max al rango efectivo
                        // de los datos.
                        type: "value",
                        position: "right",
                        scale: true,
                        min: 0,
                        axisLine: { lineStyle: { color: this.colorShare } },
                        axisTick: { show: false },
                        // Solo el eje izquierdo dibuja la grid horizontal —
                        // duplicarla desde el eje derecho mete líneas que no
                        // coinciden con las del eje de montos.
                        splitLine: { show: false },
                        axisLabel: {
                            color: this.colorShare,
                            fontSize: 11,
                            formatter: "{value}%",
                        },
                    },
                ],
                series: [
                    {
                        name: "Monto ($)",
                        type: "bar",
                        yAxisIndex: 0,
                        data: this.amounts,
                        itemStyle: {
                            color: this.colorAmount,
                            borderRadius: [4, 4, 0, 0],
                        },
                        barMaxWidth: 38,
                    },
                    {
                        name: "(%) de Ventas",
                        type: "line",
                        yAxisIndex: 1,
                        data: this.sharePercentages,
                        smooth: false,
                        symbol: "circle",
                        symbolSize: 8,
                        connectNulls: true,
                        itemStyle: { color: this.colorShare },
                        lineStyle: { color: this.colorShare, width: 2 },
                    },
                ],
            };
        },
    },

    methods: {
        toggleExpanded() {
            this.isExpanded = !this.isExpanded;
        },
    },

    mounted() {
        const root = getComputedStyle(document.documentElement);
        const read = (token) => root.getPropertyValue(token).trim();
        this.colorAmount = read("--report-chart-accent");
        // Share line en gris medio: el accent ya está tomado por las barras y
        // la línea queda como contexto secundario, no protagonista.
        this.colorShare = read("--report-text-muted-strong");
        this.colorAxis = read("--report-border");
        this.colorAxisLabel = read("--report-text-muted");
        this.colorGrid = read("--report-divider-soft");
        this.colorTooltipBg = read("--report-bg");
        this.colorTooltipBorder = read("--report-border");
        this.chartReady = Boolean(this.colorAmount && this.colorShare);
    },
};
</script>

<style scoped lang="scss">
.history_card {
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    overflow: hidden;
}

.card_header_row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
}

.card_toggle {
    display: inline-flex;
    align-items: center;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    color: var(--report-text);

    &:hover {
        color: var(--report-text-muted-strong);
    }
}

.header_icon {
    width: 18px;
    height: 18px;
    color: var(--report-text-muted-strong);
}

.card_title {
    font-size: 14px;
    font-weight: 600;
    color: var(--report-text);
}

.chevron {
    width: 14px;
    height: 14px;
    color: var(--report-text-muted-strong);
    transition: transform 180ms ease;

    &.is-rotated {
        transform: rotate(180deg);
    }
}

.card_body {
    padding: 0 20px 20px 20px;
    border-top: 1px solid var(--report-divider-soft);
    padding-top: 12px;
}

.comparison_btn {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 12px;
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: var(--report-text-muted-strong);
    cursor: pointer;
    transition: background-color 120ms ease;

    &:hover {
        background: var(--report-surface);
    }

    &[disabled] {
        opacity: 0.55;
        cursor: not-allowed;
    }
}

.comparison_icon {
    width: 14px;
    height: 14px;
}

.legend_row {
    margin-bottom: 8px;
    font-size: 12px;
    color: var(--report-text-muted);
}

.legend_item {
    color: var(--report-text-muted);
    gap: 6px;
}

.legend_swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    display: inline-block;
}

.swatch_amount {
    background: var(--report-chart-accent);
}

.swatch_share {
    background: var(--report-text-muted-strong);
}

.chart_wrap {
    // Alto generoso para que las barras y la línea de % respiren — con
    // 280px el eje izquierdo (montos) quedaba apretado y los puntos de la
    // línea se confundían con los grids del eje. 360px da un balance
    // cómodo para 12 meses sin empujar la tabla fuera del viewport.
    height: 360px;
    position: relative;
}

.history_collapse-enter-active,
.history_collapse-leave-active {
    transition: opacity 180ms ease, max-height 220ms ease;
    overflow: hidden;
    max-height: 600px;
}

.history_collapse-enter,
.history_collapse-leave-to {
    opacity: 0;
    max-height: 0;
}

.chart {
    width: 100%;
    height: 100%;
}

.empty_state {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--report-text-muted);
    font-size: 13px;
    margin: 0;
}
</style>
